import * as React from "react";
import { dateiTypInfo } from "./dateiTyp";

/**
 * Kleines Datei-Symbol mit farbigem Typ-Band und Kürzel („PDF", „XLS",
 * „PNG" …) — damit man im Vorgang auf einen Blick sieht, was für eine Datei
 * dort liegt (User 22.10.2026).
 *
 * Die Form stammt aus der Dokument-Auswahl (`auswahl/DokumentAuswahl.tsx`,
 * Blatt mit Eselsohr und Band), die Einordnung und die Farben aus
 * `dateiTyp.ts` — also dieselbe Regel wie im Dokumente-Explorer. Das Kürzel
 * steht IM Symbol, weil Farbe allein für Farbfehlsichtige nichts trägt; dazu
 * nennt `title`/`aria-label` den Typ ausgeschrieben („Excel-Tabelle").
 *
 * Feste Farben sind hier gewollt — anders als das monochrome Set in
 * `redesign/kvpIcons.tsx`, das seine Farbe vom Elternteil erbt.
 */
export const DateiTypSymbol: React.FC<{
    fileName?: string | null;
    mimeType?: string | null;
    /** Kantenlänge in px (Standard 18). */
    size?: number;
    className?: string;
}> = ({ fileName, mimeType, size = 18, className }) => {
    const { kuerzel, label, farbe, art } = dateiTypInfo(fileName, mimeType);
    const titel = kuerzel ? `${label} (${kuerzel})` : label;
    return (
        <svg
            className={["dt-symbol", className].filter(Boolean).join(" ")}
            data-dateityp={art}
            width={size}
            height={size}
            viewBox="0 0 18 18"
            role="img"
            aria-label={titel}
            style={{ flex: "none" }}
        >
            <title>{titel}</title>
            <path
                d="M4.5 1.2h6.6l3.7 3.7v11.4a.5.5 0 0 1-.5.5H4.5a.5.5 0 0 1-.5-.5V1.7a.5.5 0 0 1 .5-.5Z"
                fill="#fff"
                stroke="#cfc3cd"
                strokeWidth={0.8}
            />
            <path d="M11.1 1.2v3.7h3.7" fill="none" stroke="#cfc3cd" strokeWidth={0.8} />
            {/* Das Band ragt links über das Blatt hinaus und ist so hoch, dass
                das Kürzel ab etwa 28 px gut lesbar ist (User 22.10.2026:
                „das PDF ist zu klein"). */}
            <rect x="0.6" y="8" width="15.2" height="7.4" rx="1.4" fill={farbe} />
            {kuerzel && (
                <text
                    x="8.2"
                    y="13.55"
                    fontSize="5.6"
                    fontWeight="800"
                    fill="#fff"
                    textAnchor="middle"
                    fontFamily="system-ui, -apple-system, 'Segoe UI', sans-serif"
                    {...(kuerzel.length >= 4 ? { textLength: 13.4, lengthAdjust: "spacingAndGlyphs" } : {})}
                >
                    {kuerzel}
                </text>
            )}
        </svg>
    );
};

/**
 * Die kompakte Form für enge Stellen (Fassungsliste, Anhang-Chips,
 * Hover-Vorschau, Listenzeilen): ein farbiges Kästchen mit dem Kürzel in
 * echter Schrift. Ein Blatt-Symbol in Zeilenhöhe (14–16 px) hätte ein Kürzel
 * von 4 px — unlesbar (User 22.10.2026). Ohne erkennbares Kürzel fällt sie
 * auf das kleine Blatt zurück.
 */
export const DateiTypPlakette: React.FC<{
    fileName?: string | null;
    mimeType?: string | null;
    className?: string;
}> = ({ fileName, mimeType, className }) => {
    const { kuerzel, label, farbe, art } = dateiTypInfo(fileName, mimeType);
    if (!kuerzel) return <DateiTypSymbol fileName={fileName} mimeType={mimeType} size={16} className={className} />;
    const titel = `${label} (${kuerzel})`;
    return (
        <span
            className={["dt-plakette", className].filter(Boolean).join(" ")}
            data-dateityp={art}
            role="img"
            aria-label={titel}
            title={titel}
            style={{
                flex: "none",
                display: "inline-block",
                minWidth: 26,
                padding: "0 4px",
                borderRadius: 4,
                background: farbe,
                color: "#fff",
                fontSize: 10,
                fontWeight: 800,
                lineHeight: "16px",
                letterSpacing: "0.03em",
                textAlign: "center",
                fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif",
            }}
        >
            {kuerzel}
        </span>
    );
};
